/**
 * Decision 223 — the billing schedule generator (pure; no I/O).
 *
 * Reproduces what Invoice Stack does from a deal's line items, with the two
 * things it cannot do (Mark, 2026-09-14): a per-LINE service start, and a
 * working copy billing can edit — pro rata by the day, split, add, remove.
 *
 * The rules, as answered one by one:
 *  - every line has its own service start (answer 6); one billing period per
 *    payment from that start; the period's end is the day before the next.
 *  - the FIRST invoice is dated the Create date (answer 11 — the day billing
 *    presses Create, editable); every later invoice is dated the 1st of the
 *    month BEFORE its period starts.
 *  - invoices are grouped by date, so lines that share a start share an
 *    invoice and staggered lines produce their own (answer 6).
 *  - one-off lines (one payment, no frequency) ride the first invoice with no
 *    term text — exactly as Invoice Stack renders a connection charge.
 *  - pro rata = period amount × days in the edited term ÷ 365, to the penny
 *    (answer 12). Only when the term is SHORTER than the full period — a full
 *    period is never re-derived from its day count (365 vs 366 would drift).
 *  - due = invoice date + the bill-to contact's terms (15 / 30 — answer 5).
 *
 * Dates are ISO strings (yyyy-mm-dd) throughout; no timezone arithmetic —
 * these are calendar dates, the same in London and in Xero.
 */

import { round2 } from './money.js'

export type Frequency =
  | 'one_time' | 'monthly' | 'quarterly' | 'per_six_months' | 'annually'
  | 'per_two_years' | 'per_three_years' | 'per_five_years'

export const FREQUENCY_MONTHS: Record<Frequency, number> = {
  one_time: 0, monthly: 1, quarterly: 3, per_six_months: 6, annually: 12,
  per_two_years: 24, per_three_years: 36, per_five_years: 60,
}

export const FREQUENCY_LABEL: Record<Frequency, string> = {
  one_time: 'One-off', monthly: 'Monthly', quarterly: 'Quarterly', per_six_months: 'Six-monthly', annually: 'Annually',
  per_two_years: 'Every two years', per_three_years: 'Every three years', per_five_years: 'Every five years',
}

/** HubSpot's `recurringbillingfrequency` values → ours. Blank = one-off. */
export function parseFrequency(value: string | null | undefined): Frequency {
  const v = String(value ?? '').trim().toLowerCase()
  if (v in FREQUENCY_MONTHS) return v as Frequency
  if (v === 'one_time' || v === 'onetime' || v === '') return 'one_time'
  if (v === 'yearly' || v === 'annual') return 'annually'
  if (v === 'semiannually' || v === 'semi_annually') return 'per_six_months'
  return 'one_time'
}

/**
 * The number of invoices a line raises: HubSpot's `hs_recurring_billing_number_of_payments`
 * when set, else the term (P36M) ÷ the frequency, else 1.
 */
export function paymentsFor(frequency: Frequency, numberOfPayments: number | null, termMonths: number | null): number {
  if (frequency === 'one_time') return 1
  if (numberOfPayments && numberOfPayments > 0) return Math.floor(numberOfPayments)
  const step = FREQUENCY_MONTHS[frequency]
  if (termMonths && termMonths > 0 && step > 0) return Math.max(1, Math.round(termMonths / step))
  return 1
}

export interface BillingLine {
  id: string
  name: string
  sku: string | null
  nominalCode: string | null
  qty: number
  /** Price per billing period (the HubSpot unit price — annual for annual lines). LIST price: the discount below is applied on top. */
  unitPrice: number
  /** Decision 235: HubSpot's line discount — a percentage (`hs_discount_percentage`) … (absent = 0) */
  discountPct?: number
  /** … or a per-unit amount (`discount`). Both zero = no discount. */
  discountUnit?: number
  frequency: Frequency
  payments: number
  /** ISO date; null = falls back to the profile's contract start / create date. */
  serviceStart: string | null
  /** Answer 13: untick a line on an interim bill and it is left out of the schedule. */
  billed: boolean
}

export interface ScheduleInput {
  lines: BillingLine[]
  /** The year-1 invoice date — the day Create is pressed (editable). ISO. */
  createDate: string
  /** Default service start for lines with none. ISO; null → createDate. */
  contractStart: string | null
  termsDays: number
  vatPct: number
  /** Answer 5: the Reference — `<PO> #<dealId>` or `<school> - PO: <po> #<dealId>`. */
  reference: string
  /**
   * Decision 243: later-year invoices whose dates fall within this many months
   * of each other share ONE invoice, dated for the earliest (each line keeps
   * its own term). 0 / absent = every anniversary its own invoice. The year-1
   * (create-date) invoice is never merged into — it is already every line.
   */
  groupMonths?: number
}

export interface ScheduleLine {
  lineId: string
  description: string
  sku: string | null
  nominalCode: string | null
  termFrom: string | null
  termTo: string | null
  qty: number
  /** LIST unit price — Xero shows it with the discount alongside (decision 235). */
  unitAmount: number
  discountPct?: number
  discountUnit?: number
  net: number
  prorataDays: number | null
  fullPeriodAmount: number | null
  edited: boolean
}

/**
 * Decision 235 — pure: the net unit price after HubSpot's discount. A per-unit
 * amount comes off first, then the percentage (HubSpot allows one or the
 * other; if both ever arrive, this is the only sane order). Never below zero.
 */
export function netUnit(unitPrice: number, discountPct = 0, discountUnit = 0): number {
  const pct = Number.isFinite(discountPct) ? Math.min(100, Math.max(0, discountPct)) : 0
  const off = Number.isFinite(discountUnit) ? Math.max(0, discountUnit) : 0
  return round2(Math.max(0, (unitPrice - off) * (1 - pct / 100)))
}

/** Decision 235: a full period's net for a line — qty × net unit. */
export function lineFullAmount(l: { qty: number; unitPrice: number; discountPct?: number; discountUnit?: number }): number {
  return round2(l.qty * netUnit(l.unitPrice, l.discountPct ?? 0, l.discountUnit ?? 0))
}

export interface ScheduleInvoice {
  seq: number
  invoiceDate: string
  dueDate: string
  reference: string
  lines: ScheduleLine[]
  net: number
  tax: number
  gross: number
  edited: boolean
}

// ---------------------------------------------------------------- dates

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/

export function isIsoDate(s: unknown): s is string {
  if (typeof s !== 'string' || !ISO.test(s)) return false
  const d = new Date(`${s}T00:00:00Z`)
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s
}

function parts(iso: string): [number, number, number] {
  const m = ISO.exec(iso)
  if (!m) throw new Error(`not an ISO date: ${iso}`)
  return [Number(m[1]), Number(m[2]), Number(m[3])]
}

function toIso(y: number, m: number, d: number): string {
  return new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10)
}

/** Add whole months, clamping the day to the target month's length (31 Jan + 1 → 28/29 Feb). */
export function addMonths(iso: string, months: number): string {
  const [y, m, d] = parts(iso)
  const target = new Date(Date.UTC(y, m - 1 + months, 1))
  const daysInTarget = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate()
  return toIso(target.getUTCFullYear(), target.getUTCMonth() + 1, Math.min(d, daysInTarget))
}

export function addDays(iso: string, days: number): string {
  const [y, m, d] = parts(iso)
  return toIso(y, m, d + days)
}

/** Inclusive day count from → to (1 Jan → 31 Dec = 365). */
export function daysBetween(fromIso: string, toIso_: string): number {
  const [fy, fm, fd] = parts(fromIso)
  const [ty, tm, td] = parts(toIso_)
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000) + 1
}

/** Answer 11: the 1st of the month BEFORE the period starts. */
export function invoiceDateFor(periodStart: string): string {
  const [y, m] = parts(periodStart)
  return addMonths(toIso(y, m, 1), -1)
}

/** dd/mm/yyyy — the Term text Xero shows (`Term : { 16/09/2026 - 15/09/2027 }`). */
export function ukDate(iso: string): string {
  const [y, m, d] = parts(iso)
  return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`
}

export function termText(from: string | null, to: string | null): string {
  return from && to ? ` Term : { ${ukDate(from)} - ${ukDate(to)} }` : ''
}

/**
 * Decision 244 (Mark, 2026-09-19): what Xero prints after the term on a
 * pro-rata line — the customer sees why the amount is not the full period.
 */
export function prorataNote(days: number | null | undefined): string {
  return days == null ? '' : ` (pro rata ${days} of 365 days)`
}

/** Xero takes unit amounts to four decimal places — a pro-rated unit lands on the penny at any quantity. */
export function round4(n: number): number {
  return Math.round(n * 10000) / 10000
}

// ---------------------------------------------------------------- money

/**
 * Answer 12: pro rata by the day. `fullAmount` is the period's price (qty ×
 * unit); the edited term's day count over 365, to the penny. A term at or
 * beyond a full period returns the full amount untouched (no 366-day drift).
 */
export function prorata(fullAmount: number, termFrom: string, termTo: string, periodFrom: string, periodTo: string): { net: number; days: number | null } {
  const days = daysBetween(termFrom, termTo)
  const periodDays = daysBetween(periodFrom, periodTo)
  if (days <= 0) return { net: 0, days: 0 }
  if (days >= periodDays) return { net: round2(fullAmount), days: null }
  return { net: round2(fullAmount * days / 365), days }
}

export function totals(lines: Array<{ net: number }>, vatPct: number): { net: number; tax: number; gross: number } {
  const net = round2(lines.reduce((s, l) => s + l.net, 0))
  const tax = round2(net * vatPct / 100)
  return { net, tax, gross: round2(net + tax) }
}

// ---------------------------------------------------------------- generator

/**
 * The GENERATED schedule: every billed line's periods, grouped into invoices
 * by date, first invoice dated `createDate`. Deterministic — the same input
 * gives the same seqs, so the working copy can be diffed against it.
 */
export function generateSchedule(input: ScheduleInput): ScheduleInvoice[] {
  if (!isIsoDate(input.createDate)) throw new Error('createDate must be an ISO date')
  const defaultStart = input.contractStart && isIsoDate(input.contractStart) ? input.contractStart : input.createDate
  const byDate = new Map<string, ScheduleLine[]>()
  const push = (date: string, line: ScheduleLine) => {
    const list = byDate.get(date) ?? []
    list.push(line)
    byDate.set(date, list)
  }

  for (const line of input.lines) {
    if (!line.billed) continue
    const start = line.serviceStart && isIsoDate(line.serviceStart) ? line.serviceStart : defaultStart
    const fullAmount = lineFullAmount(line)
    const disc = { discountPct: line.discountPct ?? 0, discountUnit: line.discountUnit ?? 0 }
    if (line.frequency === 'one_time' || line.payments <= 0) {
      push(input.createDate, {
        lineId: line.id, description: line.name, sku: line.sku, nominalCode: line.nominalCode,
        termFrom: null, termTo: null, qty: line.qty, unitAmount: round2(line.unitPrice), ...disc, net: fullAmount,
        prorataDays: null, fullPeriodAmount: null, edited: false,
      })
      continue
    }
    const step = FREQUENCY_MONTHS[line.frequency]
    for (let k = 0; k < line.payments; k++) {
      const from = addMonths(start, k * step)
      const to = addDays(addMonths(start, (k + 1) * step), -1)
      const date = k === 0 ? input.createDate : invoiceDateFor(from)
      push(date, {
        lineId: line.id, description: line.name, sku: line.sku, nominalCode: line.nominalCode,
        termFrom: from, termTo: to, qty: line.qty, unitAmount: round2(line.unitPrice), ...disc, net: fullAmount,
        prorataDays: null, fullPeriodAmount: null, edited: false,
      })
    }
  }

  // Decision 243: group the later-year dates. Greedy over the sorted dates:
  // an anchor takes every later date within the window, then the next
  // unclaimed date anchors its own group. The create-date invoice stands
  // apart — it already carries every line's year 1.
  const window = Math.max(0, Math.floor(input.groupMonths ?? 0))
  if (window > 0) {
    const later = [...byDate.keys()].filter((d) => d !== input.createDate).sort()
    let i = 0
    while (i < later.length) {
      const anchor = later[i]
      const limit = addMonths(anchor, window)
      let j = i + 1
      while (j < later.length && later[j] <= limit) {
        const moved = byDate.get(later[j]) ?? []
        byDate.set(anchor, [...(byDate.get(anchor) ?? []), ...moved])
        byDate.delete(later[j])
        j++
      }
      i = j
    }
  }

  const dates = [...byDate.keys()].sort()
  // The create-date invoice is always seq 1, even when a staggered line's
  // later period lands on a date before it (it cannot — invoiceDateFor is
  // always after the create date's period — but sorting makes it explicit).
  const ordered = dates.includes(input.createDate)
    ? [input.createDate, ...dates.filter((d) => d !== input.createDate)]
    : dates
  return ordered.map((date, i) => {
    const lines = byDate.get(date) ?? []
    const t = totals(lines, input.vatPct)
    return {
      seq: i + 1, invoiceDate: date, dueDate: addDays(date, input.termsDays), reference: input.reference,
      lines, ...t, edited: false,
    }
  })
}

// ---------------------------------------------------------------- edits on the working copy

/**
 * Re-price one working line after its term was edited: pro rata against the
 * ORIGINAL period the generator gave it (or, for a hand-added line, against
 * the term itself — a full period). Returns the line with net, prorataDays
 * and fullPeriodAmount set and `edited` true. Callers pass the generated
 * counterpart when there is one.
 */
export function repriceLine(
  line: ScheduleLine, generated: ScheduleLine | null,
): ScheduleLine {
  const fullAmount = round2(line.qty * netUnit(line.unitAmount, line.discountPct ?? 0, line.discountUnit ?? 0))
  if (!line.termFrom || !line.termTo) {
    return { ...line, net: fullAmount, prorataDays: null, fullPeriodAmount: null, edited: true }
  }
  const periodFrom = generated?.termFrom ?? line.termFrom
  const periodTo = generated?.termTo ?? line.termTo
  const p = prorata(fullAmount, line.termFrom, line.termTo, periodFrom, periodTo)
  return {
    ...line, net: p.net, prorataDays: p.days, fullPeriodAmount: p.days == null ? null : fullAmount, edited: true,
  }
}

/** Recompute an invoice's totals and its `edited` flag from its lines. */
export function settleInvoice(inv: ScheduleInvoice, vatPct: number, generated: ScheduleInvoice | null): ScheduleInvoice {
  const t = totals(inv.lines, vatPct)
  const edited = !generated
    || generated.invoiceDate !== inv.invoiceDate
    || generated.lines.length !== inv.lines.length
    || inv.lines.some((l) => l.edited)
    || t.net !== generated.net
  return { ...inv, ...t, edited }
}

/** The human formula shown beside a pro-rated amount. */
export function prorataFormula(line: ScheduleLine): string | null {
  if (line.prorataDays == null || line.fullPeriodAmount == null) return null
  return `£${line.fullPeriodAmount.toFixed(2)} × ${line.prorataDays} ÷ 365 = £${line.net.toFixed(2)}`
}

// ---------------------------------------------------------------- bill-to + exit rule

/**
 * Answer 5: who is billed. `inv_company` on the deal names another entity
 * (Everything ICT…); otherwise the school. The Reference carries the school
 * and its PO when an entity is billed (Invoice Stack's shape), the PO alone
 * otherwise — always ending `#<dealId>`, the link back to the deal.
 */
export function billingReference(input: { dealId: string; poRef: string | null; schoolName: string | null; viaEntity: boolean }): string {
  // The HubSpot PO often ALREADY carries the deal id ("NA #57447745840" —
  // Invoice Stack's own habit); strip it so the id appears exactly once.
  const po = stripDealTag((input.poRef ?? '').trim(), input.dealId)
  const school = (input.schoolName ?? '').trim()
  const parts: string[] = []
  if (input.viaEntity && school) parts.push(po ? `${school} - PO: ${po}` : school)
  else if (po) parts.push(po)
  parts.push(`#${input.dealId}`)
  return parts.join(' ')
}

/** Remove a trailing `#<dealId>` (any spacing) from a PO reference. */
export function stripDealTag(po: string, dealId: string): string {
  return po.replace(new RegExp(`\\s*#\\s*${dealId}\\s*$`), '').trim()
}

export type Destination = 'contract_import' | 'invoiced_setup' | 'invoiced_monthly'

/**
 * Answer 9: renewable (any line with more than one payment or a recurring
 * frequency) → Contract Import; monthly-only additions → Invoiced - Additions /
 * Monthly Contracts; everything one-off → Invoiced - Setup / One Off.
 */
export function destinationFor(lines: BillingLine[]): Destination {
  const billed = lines.filter((l) => l.billed)
  const recurring = billed.filter((l) => l.frequency !== 'one_time')
  if (recurring.length === 0) return 'invoiced_setup'
  if (recurring.every((l) => l.frequency === 'monthly')) return 'invoiced_monthly'
  return 'contract_import'
}

/** The contract window the profile implies: earliest line start → latest period end. */
export function contractWindow(lines: BillingLine[], defaultStart: string): { start: string; end: string; termMonths: number } | null {
  let start: string | null = null
  let end: string | null = null
  for (const l of lines) {
    if (!l.billed || l.frequency === 'one_time') continue
    const s = l.serviceStart && isIsoDate(l.serviceStart) ? l.serviceStart : defaultStart
    const e = addDays(addMonths(s, l.payments * FREQUENCY_MONTHS[l.frequency]), -1)
    if (!start || s < start) start = s
    if (!end || e > end) end = e
  }
  if (!start || !end) return null
  const [sy, sm] = parts(start)
  const [ey, em] = parts(addDays(end, 1))
  return { start, end, termMonths: (ey - sy) * 12 + (em - sm) }
}


// ---------------------------------------------------------------- decision 223g: align a later-billed line to the existing drafts

export type AlignMode = 'own' | 'align' | 'align_prorata'

export interface ExistingDraft {
  seq: number
  invoiceDate: string
  /** The period the draft bills (from the terms of its lines). */
  periodFrom: string
  periodTo: string
}

export interface AlignedPlan {
  /** Year 1 — its own invoice (dated on Create). Null when the line has nothing before the drafts. */
  first: { termFrom: string; termTo: string; line: ScheduleLine } | null
  /** Periods added to existing drafts, in date order. */
  merged: Array<{ seq: number; line: ScheduleLine }>
  /** Periods left after the drafts run out — generated as own invoices from `leftoverStart`. */
  leftover: number
  leftoverStart: string | null
}

/**
 * Mark (2026-09-14 pm): "although an individual new invoice will be needed for
 * the first year, the line could be added to the year 2 / 3 drafts" — and then:
 * "we don't always pro-rata (first invoice or the rest) even if they are
 * attaching to existing drafts.. We end up with staggered dates on a single
 * invoice, but that's OK".
 *
 * `align`         — the line keeps its OWN periods (start, start + 12 m, …).
 *                   Period 1 is its own invoice, full amount. Each later period
 *                   is added to the open DRAFT whose period CONTAINS the period's
 *                   start (so a 1 Oct line rides the 27 Aug → 26 Aug draft with
 *                   its own 1 Oct → 30 Sep term text). A period no draft covers
 *                   becomes its own invoice.
 * `align_prorata` — period 1 is cut at the contract's next anniversary (the
 *                   first open draft that starts after the line) and pro-rated
 *                   by the day; each later period takes the draft's own period.
 */
export function alignToDrafts(line: BillingLine, start: string, drafts: ExistingDraft[], mode: 'align' | 'align_prorata' = 'align'): AlignedPlan {
  const mk = (from: string | null, to: string | null, net: number, prorataDays: number | null, full: number | null): ScheduleLine => ({
    lineId: line.id, description: line.name, sku: line.sku, nominalCode: line.nominalCode,
    termFrom: from, termTo: to, qty: line.qty, unitAmount: round2(line.unitPrice), discountPct: line.discountPct ?? 0, discountUnit: line.discountUnit ?? 0, net, prorataDays, fullPeriodAmount: full, edited: false,
  })
  const step = FREQUENCY_MONTHS[line.frequency]
  const fullAmount = lineFullAmount(line)
  const sorted = [...drafts].sort((a, b) => a.periodFrom.localeCompare(b.periodFrom))
  if (line.frequency === 'one_time' || line.payments <= 0 || sorted.length === 0) {
    return { first: null, merged: [], leftover: line.payments, leftoverStart: start }
  }

  if (mode === 'align') {
    const merged: AlignedPlan['merged'] = []
    const firstTo = addDays(addMonths(start, step), -1)
    const first = { termFrom: start, termTo: firstTo, line: mk(start, firstTo, fullAmount, null, null) }
    const used = new Set<number>()
    let leftover = 0
    let leftoverStart: string | null = null
    for (let k = 1; k < line.payments; k++) {
      const from = addMonths(start, k * step)
      const to = addDays(addMonths(start, (k + 1) * step), -1)
      const host = sorted.find((d) => !used.has(d.seq) && d.periodFrom <= from && from <= d.periodTo)
      if (host && leftover === 0) { used.add(host.seq); merged.push({ seq: host.seq, line: mk(from, to, fullAmount, null, null) }); continue }
      // once a period has no draft, this and every later period are own invoices (keeps the sequence contiguous)
      if (leftover === 0) leftoverStart = from
      leftover++
    }
    return { first, merged, leftover, leftoverStart }
  }

  const candidates = sorted.filter((d) => d.periodFrom > start)
  if (candidates.length === 0) return { first: null, merged: [], leftover: line.payments, leftoverStart: start }
  let remaining = line.payments
  const cutTo = addDays(candidates[0].periodFrom, -1)
  const fullTo = addDays(addMonths(start, step), -1)
  const pr = prorata(fullAmount, start, cutTo, start, fullTo)
  const first = { termFrom: start, termTo: cutTo, line: mk(start, cutTo, pr.net, pr.days, pr.days == null ? null : fullAmount) }
  remaining--
  const merged: AlignedPlan['merged'] = []
  let last: ExistingDraft | null = null
  for (const d of candidates) {
    if (remaining <= 0) break
    merged.push({ seq: d.seq, line: mk(d.periodFrom, d.periodTo, fullAmount, null, null) })
    remaining--
    last = d
  }
  return { first, merged, leftover: remaining, leftoverStart: remaining > 0 && last ? addDays(last.periodTo, 1) : null }
}

// ---------------------------------------------------------------- decision 225: contract rollover

export interface RolloverPlan {
  /** The continuation's first period start — the day after the line's last period ends. */
  serviceStart: string
  /** Whole billing periods covering the extension (12 months on a quarterly line = 4; on a two-year line = 1). */
  payments: number
  /** The extension's last day. */
  periodEnd: string
  /** The invoice date of the first continuation invoice: the 1st of the month before it starts (answer 11), never before `today`. */
  invoiceDate: string
}

/**
 * Decision 225 (Mark 2026-09-15): a Process Rollover deal extends its
 * renewable lines by N months (typically 12). The continuation starts where
 * the line's last period ended and keeps the line's own cadence; a one-off
 * line, or one that never billed, has nothing to roll — null.
 */
export function planRollover(line: Pick<BillingLine, 'frequency' | 'payments' | 'serviceStart'>, months: number, defaultStart: string, today: string): RolloverPlan | null {
  if (line.frequency === 'one_time' || line.payments <= 0 || months <= 0) return null
  const step = FREQUENCY_MONTHS[line.frequency]
  const start = line.serviceStart && isIsoDate(line.serviceStart) ? line.serviceStart : defaultStart
  const serviceStart = addMonths(start, line.payments * step)
  const payments = Math.max(1, Math.ceil(months / step))
  const periodEnd = addDays(addMonths(serviceStart, payments * step), -1)
  const natural = invoiceDateFor(serviceStart)
  return { serviceStart, payments, periodEnd, invoiceDate: natural < today ? today : natural }
}
